/**
 * Hook useCrmDealsRealtime - Real-time orientado a eventos para Kanban CRM
 * Implementa padrão enterprise: atualização granular do cache sem re-fetch
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-30 v1.2.0 - Sincroniza header (count) e lista (cards) das colunas:
 *            além do setQueryData otimista, invalida o cache da(s) coluna(s) afetada(s)
 *            (`kanban-column`) junto com `pipeline-phase-stats` e `kanban-column-counts`.
 *            Elimina janela em que o header mostra +1 antes do card aparecer (caso de
 *            cache vazio ou payload realtime sem joins enriquecidos).
 *   RELEASE: 2026-xx-xx v1.0.0 - Versão inicial.
 *
 * Eventos processados:
 * - DEAL_CREATED: Adiciona deal no topo da coluna
 * - DEAL_MOVED: Move deal entre colunas (atualiza origem e destino)
 * - DEAL_STATUS_CHANGED: Trata mudanças de status (won/lost/reopen)
 * - DEAL_UPDATED: Atualiza dados do deal na coluna atual
 * - DEAL_DELETED: Remove deal da coluna
 */
import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient, InfiniteData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { KanbanDeal } from './useKanbanColumn';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

// Logger para depuração
const logger = createLogger('CrmDealsRealtime');

// Tipo do payload de evento
interface DealPayload {
  id: string;
  title: string;
  description: string | null;
  company_name: string | null;
  value: number;
  currency: string;
  priority: string;
  phase_id: string;
  pipeline_id: string;
  status: 'open' | 'won' | 'lost';
  contact_id: string | null;
  assigned_to: string | null;
  expected_close_date: string | null;
  updated_at: string;
  created_at: string;
  tenant_id: string;
}

// Payload do evento de mudança de contrato (v1.1.0)
interface ContractPayload {
  id: string;
  deal_id: string;
  status: string;
  title: string;
  tenant_id: string;
}

// Tipo de evento detectado
type DealEventType = 
  | 'DEAL_CREATED' 
  | 'DEAL_MOVED' 
  | 'DEAL_STATUS_CHANGED' 
  | 'DEAL_UPDATED' 
  | 'DEAL_DELETED';

/**
 * Detecta o tipo de evento baseado na mudança
 */
function detectEventType(
  eventType: 'INSERT' | 'UPDATE' | 'DELETE',
  oldRecord: DealPayload | null,
  newRecord: DealPayload | null
): DealEventType {
  if (eventType === 'INSERT') return 'DEAL_CREATED';
  if (eventType === 'DELETE') return 'DEAL_DELETED';
  
  // UPDATE - verificar o que mudou
  if (oldRecord && newRecord) {
    // Mudou de fase?
    if (oldRecord.phase_id !== newRecord.phase_id) {
      return 'DEAL_MOVED';
    }
    // Mudou de status?
    if (oldRecord.status !== newRecord.status) {
      return 'DEAL_STATUS_CHANGED';
    }
  }
  
  return 'DEAL_UPDATED';
}

/**
 * Converte payload do banco para formato do Kanban
 * v1.3.0: Adicionados campos do seller
 */
function payloadToKanbanDeal(payload: DealPayload): KanbanDeal {
  return {
    id: payload.id,
    title: payload.title,
    description: payload.description,
    company_name: payload.company_name,
    value: payload.value,
    currency: payload.currency,
    priority: payload.priority,
    expected_close_date: payload.expected_close_date,
    contact_id: payload.contact_id,
    contact_name: null, // Será preenchido se necessário
    contact_avatar: null,
    contact_phone: null, // Campo adicionado na v1.1.0
    assigned_to: payload.assigned_to,
    assigned_user_name: null, // Será preenchido se necessário
    assigned_user_avatar: null,
    // v1.3.0: Campos do vendedor (serão preenchidos pelo RPC se necessário)
    seller_id: (payload as any).seller_id || null,
    seller_name: null,
    seller_avatar: null,
    // v1.4.0: Campos adicionais para o card
    source: (payload as any).source || null,
    probability: (payload as any).probability || null,
    updated_at: payload.updated_at,
    created_at: payload.created_at,
    // v1.5.0: tenant_id para corrigir bug de tags no modal do card
    tenant_id: payload.tenant_id,
    // v1.6.0: status para exibir corretamente no card
    status: (payload as any).status || 'open',
    // v1.9.0: Campos de contrato (null no realtime — preenchidos pela RPC)
    last_contract_status: null,
    last_contract_title: null,
    // v2.0.0: customer_id (null no realtime — preenchido pela RPC)
    customer_id: (payload as any).customer_id || null,
    // v2.2.0: Tags vazias no realtime (preenchidas pela RPC)
    tags: [],
    // v2.3.0: phase_updated_at (null no realtime — preenchido pela RPC)
    phase_updated_at: (payload as any).phase_updated_at || null,
    // v2.2.0: agregação de cadência per_phase (zerada no realtime — preenchida pela RPC)
    cadence_phase_total: 0,
    cadence_phase_pending: 0,
    cadence_phase_has_overdue: false,
  };
}

/**
 * Hook principal de real-time para CRM Deals
 * Escuta eventos do pipeline específico e atualiza cache localmente
 * 
 * @param pipelineId - ID do pipeline a monitorar (null desabilita)
 */
export function useCrmDealsRealtime(pipelineId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  /**
   * Adiciona deal no cache de uma coluna
   */
  const addDealToColumn = useCallback((phaseId: string, deal: KanbanDeal) => {
    queryClient.setQueryData<InfiniteData<KanbanDeal[]>>(
      ['kanban-column', pipelineId, phaseId],
      (oldData) => {
        if (!oldData) return oldData;
        
        // Adiciona no início da primeira página
        const newPages = [...oldData.pages];
        if (newPages.length > 0) {
          // Evita duplicatas
          const existsInFirstPage = newPages[0].some(d => d.id === deal.id);
          if (!existsInFirstPage) {
            newPages[0] = [deal, ...newPages[0]];
          }
        } else {
          newPages.push([deal]);
        }
        
        return { ...oldData, pages: newPages };
      }
    );
  }, [queryClient, pipelineId]);

  /**
   * Remove deal do cache de uma coluna
   */
  const removeDealFromColumn = useCallback((phaseId: string, dealId: string) => {
    queryClient.setQueryData<InfiniteData<KanbanDeal[]>>(
      ['kanban-column', pipelineId, phaseId],
      (oldData) => {
        if (!oldData) return oldData;
        
        const newPages = oldData.pages.map(page => 
          page.filter(deal => deal.id !== dealId)
        );
        
        return { ...oldData, pages: newPages };
      }
    );
  }, [queryClient, pipelineId]);

  /**
   * Atualiza deal no cache de uma coluna
   */
  const updateDealInColumn = useCallback((phaseId: string, dealId: string, updates: Partial<KanbanDeal>) => {
    queryClient.setQueryData<InfiniteData<KanbanDeal[]>>(
      ['kanban-column', pipelineId, phaseId],
      (oldData) => {
        if (!oldData) return oldData;
        
        const newPages = oldData.pages.map(page =>
          page.map(deal => 
            deal.id === dealId 
              ? { ...deal, ...updates }
              : deal
          )
        );
        
        return { ...oldData, pages: newPages };
      }
    );
  }, [queryClient, pipelineId]);

  /**
   * Invalida stats do pipeline (contadores do header das fases)
   */
  const invalidateStats = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['pipeline-phase-stats', pipelineId],
    });
    // v1.2.0: também invalida contagens com filtros aplicados (usado quando há filtros ativos)
    queryClient.invalidateQueries({
      queryKey: ['kanban-column-counts', pipelineId],
    });
  }, [queryClient, pipelineId]);

  /**
   * v1.2.0: Invalida o cache da coluna (lista de cards) de uma fase específica.
   * Usado em conjunto com `invalidateStats` para garantir que header e cards
   * sejam refetchados no mesmo tick — eliminando dessincronia visual em que
   * a contagem aparece antes do card.
   */
  const invalidateColumn = useCallback((phaseId: string) => {
    if (!phaseId) return;
    queryClient.invalidateQueries({
      queryKey: ['kanban-column', pipelineId, phaseId],
    });
  }, [queryClient, pipelineId]);

  /**
   * Handler de eventos de deal
   */
  const handleDealEvent = useCallback((
    payload: RealtimePostgresChangesPayload<DealPayload>
  ) => {
    const eventType = payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE';
    const newRecord = payload.new as DealPayload | null;
    const oldRecord = payload.old as DealPayload | null;

    // Ignora se não for do pipeline atual
    const dealPipelineId = newRecord?.pipeline_id || oldRecord?.pipeline_id;
    if (dealPipelineId !== pipelineId) {
      return;
    }

    const dealEventType = detectEventType(eventType, oldRecord, newRecord);
    
    logger.info(`Evento real-time: ${dealEventType}`, {
      dealId: newRecord?.id || oldRecord?.id,
      oldPhase: oldRecord?.phase_id?.substring(0, 8),
      newPhase: newRecord?.phase_id?.substring(0, 8),
    });

    switch (dealEventType) {
      case 'DEAL_CREATED': {
        if (!newRecord) break;
        const kanbanDeal = payloadToKanbanDeal(newRecord);
        // Atualização otimista imediata (cobre caso em que cache já existe)
        addDealToColumn(newRecord.phase_id, kanbanDeal);
        // v1.2.0: refetch da coluna garante dados enriquecidos (joins) e cobre cache vazio
        invalidateColumn(newRecord.phase_id);
        invalidateStats();
        break;
      }

      case 'DEAL_MOVED': {
        if (!oldRecord || !newRecord) break;
        const kanbanDeal = payloadToKanbanDeal(newRecord);
        // Remove da coluna antiga
        removeDealFromColumn(oldRecord.phase_id, oldRecord.id);
        // Adiciona na nova coluna
        addDealToColumn(newRecord.phase_id, kanbanDeal);
        // v1.2.0: invalida ambas as colunas envolvidas para sincronia com header
        invalidateColumn(oldRecord.phase_id);
        invalidateColumn(newRecord.phase_id);
        invalidateStats();
        break;
      }

      case 'DEAL_STATUS_CHANGED': {
        if (!oldRecord || !newRecord) break;
        // v1.6.1: Passa status atualizado para o cache do card
        const updatedDeal = payloadToKanbanDeal(newRecord);
        updateDealInColumn(newRecord.phase_id, newRecord.id, updatedDeal);
        // v1.2.0: status muda contagens (won/lost) e pode alterar visibilidade
        invalidateColumn(newRecord.phase_id);
        invalidateStats();
        break;
      }

      case 'DEAL_UPDATED': {
        if (!newRecord) break;
        const updates = payloadToKanbanDeal(newRecord);
        updateDealInColumn(newRecord.phase_id, newRecord.id, updates);
        break;
      }

      case 'DEAL_DELETED': {
        if (!oldRecord) break;
        removeDealFromColumn(oldRecord.phase_id, oldRecord.id);
        // v1.2.0: refetch da coluna para sincronizar com header invalidado
        invalidateColumn(oldRecord.phase_id);
        invalidateStats();
        break;
      }
    }
  }, [pipelineId, addDealToColumn, removeDealFromColumn, updateDealInColumn, invalidateColumn, invalidateStats]);

  /**
   * Setup do canal real-time
   */
  useEffect(() => {
    // Precisa de pipelineId e tenant para monitorar
    if (!pipelineId || !currentTenant?.id) {
      return;
    }

    // Cleanup do canal anterior se existir
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Cria canal segmentado por pipeline
    const channelName = `crm-deals:${pipelineId}`;
    logger.info(`Iniciando canal real-time: ${channelName}`);

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'crm_deals',
          filter: `pipeline_id=eq.${pipelineId}`,
        },
        (payload) => {
          handleDealEvent(payload as RealtimePostgresChangesPayload<DealPayload>);
        }
      )
      // v1.1.0: Segundo listener — escuta mudanças de status na tabela contracts
      // Atualiza last_contract_status e last_contract_title nos cards do Kanban
      // sem refetch, localizando o deal em qualquer coluna do pipeline ativo
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contracts',
          filter: `tenant_id=eq.${currentTenant?.id}`,
        },
        (payload) => {
          const newData = payload.new as ContractPayload;
          if (!newData.deal_id) return;

          // Percorre todos os caches de colunas do pipeline ativo
          // para localizar o deal sem precisar saber o phase_id
          const allQueries = queryClient.getQueryCache().findAll({
            queryKey: ['kanban-column', pipelineId],
          });

          for (const query of allQueries) {
            const data = query.state.data as InfiniteData<KanbanDeal[]> | undefined;
            if (!data) continue;

            // Verifica se o deal está nesta coluna
            const dealExists = data.pages.some(page =>
              page.some(deal => deal.id === newData.deal_id)
            );
            if (!dealExists) continue;

            // Atualiza apenas os campos de contrato, preservando todos os outros dados do card
            queryClient.setQueryData<InfiniteData<KanbanDeal[]>>(
              query.queryKey,
              (oldData) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  pages: oldData.pages.map(page =>
                    page.map(deal =>
                      deal.id === newData.deal_id
                        ? {
                            ...deal,
                            last_contract_status: newData.status,
                            last_contract_title: newData.title,
                          }
                        : deal
                    )
                  ),
                };
              }
            );

            logger.info('Badge de contrato atualizado no card do Kanban', {
              dealId: newData.deal_id.substring(0, 8),
              novoStatus: newData.status,
            });
            break; // Deal encontrado e atualizado — para a busca
          }
        }
      )
      .subscribe((status) => {
        logger.info(`Canal ${channelName} status: ${status}`);
      });

    channelRef.current = channel;

    // Cleanup ao desmontar ou mudar pipeline
    return () => {
      logger.info(`Removendo canal real-time: ${channelName}`);
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [pipelineId, currentTenant?.id, handleDealEvent]);

  return null; // Hook não retorna nada, apenas gerencia side effects
}
